import React, { useState } from "react";
import { Bell, X, AlertTriangle } from "lucide-react";
import { useApp } from "../context/AppContext";
import InstallButton from "./InstallButton";
import PushButton from "./PushButton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Link } from "react-router-dom";

export default function TopBar({ title, subtitle }) {
  const { stats, lowStock } = useApp();
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const alerts = (stats?.in_esaurimento || 0) + (stats?.esauriti || 0);

  return (
    <>
      <header className="topbar" data-testid="topbar">
        <div className="min-w-0">
          <div className="eyebrow" data-testid="topbar-eyebrow">
            Magazzino
          </div>
          <h1
            className="text-xl sm:text-2xl font-semibold tracking-tight text-slate-900 truncate"
            data-testid="topbar-title"
          >
            {title}
          </h1>
          {subtitle && (
            <p className="text-sm text-slate-500 truncate">{subtitle}</p>
          )}
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <PushButton compact />
          <InstallButton compact />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="relative inline-flex items-center justify-center w-10 h-10 rounded border border-slate-200 bg-white hover:bg-slate-50 transition-colors"
                data-testid="notifications-bell"
                aria-label="Notifiche"
              >
                <Bell className="h-5 w-5 text-slate-700" />
                {alerts > 0 && (
                  <span
                    className="bell-badge"
                    data-testid="notifications-count"
                  >
                    {alerts}
                  </span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-80 p-0"
              data-testid="notifications-dropdown"
            >
              <div className="px-4 py-3 border-b border-slate-200">
                <div className="eyebrow">Notifiche</div>
                <div className="text-sm font-semibold text-slate-900 mt-0.5">
                  Prodotti da controllare
                </div>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {lowStock.length === 0 ? (
                  <div className="px-4 py-6 text-sm text-slate-500 text-center">
                    Nessuna notifica. Tutte le scorte sono sopra la soglia
                    minima.
                  </div>
                ) : (
                  lowStock.map((p) => (
                    <Link
                      key={p.id}
                      to="/prodotti"
                      className="flex items-start gap-3 px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors"
                      data-testid={`notification-item-${p.code}`}
                    >
                      <div
                        className={`mt-0.5 w-2 h-2 rounded-full ${
                          p.quantity <= 0 ? "bg-red-500" : "bg-amber-500"
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate">
                          {p.name}
                        </div>
                        <div className="text-xs text-slate-500 mono">
                          {p.code} · {p.quantity} {p.unit} (soglia{" "}
                          {p.min_threshold})
                        </div>
                      </div>
                      <span
                        className={`pill ${
                          p.quantity <= 0 ? "pill-danger" : "pill-warn"
                        }`}
                      >
                        {p.quantity <= 0 ? "Esaurito" : "Basso"}
                      </span>
                    </Link>
                  ))
                )}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {alerts > 0 && !bannerDismissed && (
        <div
          className="mx-4 sm:mx-6 mt-4 rounded border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3"
          data-testid="low-stock-banner"
        >
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-amber-900">
            <strong className="font-semibold">
              Attenzione: {alerts} prodott{alerts === 1 ? "o" : "i"} sotto
              soglia
            </strong>
            <span className="ml-2 text-amber-800">
              Controlla la lista riordino per procedere.
            </span>
          </div>
          <Link
            to="/prodotti"
            className="text-xs font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-950"
            data-testid="banner-goto-products"
          >
            Vai ai prodotti
          </Link>
          <button
            onClick={() => setBannerDismissed(true)}
            className="text-amber-700 hover:text-amber-900"
            aria-label="Chiudi"
            data-testid="banner-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </>
  );
}
